import { handle } from 'redux-pack';
import { Action } from '../../../types/actions';
import { AnalyticsEvent, AnalyticsSession } from '../../../types/working-model';
import { GET_CHANNEL_AUDIENCE, GET_CHANNEL_BROWSERS, GET_CHANNEL_DEVICE_TYPES, GET_CHANNEL_DURATION, GET_CHANNEL_EVENTS, GET_CHANNEL_REGIONS, GET_CHANNEL_REGISTRATIONS, GET_CHANNEL_RETENTION_AVERAGE, GET_CHANNEL_RETENTION_BUCKETS, GET_CHANNEL_VIEWS, GET_CHANNEL_WATCHTIME, GET_EVENT_BROWSERS, GET_EVENT_DEVICE_TYPES, GET_EVENT_DURATION, GET_EVENT_EMAILS, GET_EVENT_LANGUAGES, GET_EVENT_REGIONS, GET_EVENT_RETENTION_AVERAGE, GET_EVENT_RETENTION_BUCKETS, GET_EVENT_SESSIONS, GET_EVENT_VIEWS, GET_EVENT_WATCHTIME, GET_SESSION_BROWSERS, GET_SESSION_DEVICE_TYPES, GET_SESSION_DURATION, GET_SESSION_EMAILS, GET_SESSION_LANGUAGES, GET_SESSION_REGIONS, GET_SESSION_REGISTRATIONS, GET_SESSION_RETENTION_AVERAGE, GET_SESSION_TIMELINE, GET_SESSION_VIEWS, GET_SESSION_WATCHTIME, GET_EVENT_REGISTRATION_ANALYTICS, GET_EVENT_COUNTRY_MAP, GET_SESSION_COUNTRY_MAP, GET_HIGHEST_CONCURRENT, GET_USER_SURVEY, SET_TIME_RANGE, SET_AUDIENCE_LISTS_FILTER } from '../../actions/admin/analytics';

interface Wrapper<T> {
	loading?: boolean,
	error?: string | null,
	data: T
}

interface ScorecardData {
	value: number | null;
	sparkline?: Array<number>
	min?: number,
	max?: number
}

export interface PieChartData {
	value: number | string;
	label: string;
}

export interface AdminAnalyticsState {
	channelEvents: Wrapper<Array<AnalyticsEvent>>
	channelDevices: Wrapper<Array<any>>;
	channelBrowsers: Wrapper<Array<any>>;
	channelAudience: Wrapper<ScorecardData>;
	channelRegistrations: Wrapper<ScorecardData>;
	channelDuration: Wrapper<ScorecardData>;
	channelViews: Wrapper<ScorecardData>;
	channelWatchtime: Wrapper<ScorecardData>;
	channelRetentionAverage: Wrapper<ScorecardData>;
	channelRetentionBuckets: Wrapper<Array<any>>;
	channelRegions: Wrapper<Array<any>>;
	eventDevices: Wrapper<Array<any>>;
	eventRegions: Wrapper<Array<any>>;
	eventLanguages: Wrapper<Array<any>>;
	eventViews: Wrapper<ScorecardData>;
	eventBrowsers: Wrapper<Array<any>>;
	eventAudience: Wrapper<ScorecardData>;
	eventRegistrations: Wrapper<ScorecardData>;
	eventEmails: Wrapper<ScorecardData>;
	eventDuration: Wrapper<ScorecardData>;
	eventWatchtime: Wrapper<ScorecardData>;
	eventRetentionAverage: Wrapper<ScorecardData>;
	eventRetentionBuckets: Wrapper<Array<any>>;
	eventSessions: Wrapper<Array<AnalyticsSession>>;
	eventMapData: Wrapper<Array<any>>;
	sessionDevices: Wrapper<Array<any>>;
	sessionUserSurvey: Wrapper<Array<PieChartData>>;
	sessionRegions: Wrapper<Array<any>>;
	sessionLanguages: Wrapper<Array<any>>;
	sessionViews: Wrapper<ScorecardData>;
	sessionBrowsers: Wrapper<Array<any>>;
	sessionAudience: Wrapper<ScorecardData>;
	sessionRegistrations: Wrapper<ScorecardData>;
	sessionEmails: Wrapper<ScorecardData>;
	sessionDuration: Wrapper<ScorecardData>;
	sessionWatchtime: Wrapper<ScorecardData>;
	sessionSessions: Wrapper<Array<any>>;
	sessionRetentionAverage: Wrapper<ScorecardData>;
	sessionTimeline: Wrapper<any>
	sessionMapData: Wrapper<Array<any>>;
	sessionHighestConcurrent: Wrapper<any>;
	timeRange: Wrapper<any>;
	audienceLists: Wrapper<Array<string>>
	// [key: string]: Wrapper<any>
}

const initialState: AdminAnalyticsState = {
	channelEvents: { data: [] },
	channelDevices: { data: [] },
	channelBrowsers: { data: [] },
	channelAudience: { data: { value: null } },
	channelRegistrations: { data: { value: null } },
	channelDuration: { data: { value: null } },
	channelViews: { data: { value: null } },
	channelWatchtime: { data: { value: null } },
	channelRetentionAverage: { data: { value: null } },
	channelRetentionBuckets: { data: [] },
	channelRegions: { data: [] },
	eventDevices: { data: [] },
	eventRegions: { data: [] },
	eventLanguages: { data: [] },
	eventViews: { data: { value: null } },
	eventBrowsers: { data: [] },
	eventAudience: { data: { value: null } },
	eventRegistrations: { data: { value: null } },
	eventEmails: { data: { value: null } },
	eventDuration: { data: { value: null } },
	eventWatchtime: { data: { value: null } },
	eventSessions: { data: [] },
	eventMapData: { data: [] },
	eventRetentionAverage: { data: { value: null } },
	eventRetentionBuckets: { data: [] },
	sessionDevices: { data: [] },
	sessionUserSurvey: { data: [] },
	sessionRegions: { data: [] },
	sessionLanguages: { data: [] },
	sessionViews: { data: { value: null } },
	sessionBrowsers: { data: [] },
	sessionAudience: { data: { value: null } },
	sessionRegistrations: { data: { value: null } },
	sessionEmails: { data: { value: null } },
	sessionDuration: { data: { value: null } },
	sessionWatchtime: { data: { value: null } },
	sessionSessions: { data: [] },
	sessionRetentionAverage: { data: { value: null } },
	sessionTimeline: { data: { value: null } },
	sessionMapData: { data: [] },
	sessionHighestConcurrent: { data: { value: null } },
	timeRange: { data: { value: null } },
	audienceLists: {
		data: []
	}
};

const defaultHandler = (state: AdminAnalyticsState, action: Action, property: keyof AdminAnalyticsState) => {
	return handle(state, action, {
		success: (state) => ({
			...state,
			[property]: {
				...state[property],
				data: action.payload,
				error: null
			}
		}),
		start: (state) => ({ ...state, [property]: { ...state[property], loading: true } }),
		failure: (state) => ({ ...state, [property]: { ...state[property], error: 'Unable to load' } }),
		finish: (state) => ({ ...state, [property]: { ...state[property], loading: false } }),
	});
};

export default function AnalyticsReducer(
	state: AdminAnalyticsState = initialState,
	action: Action
): AdminAnalyticsState {
	switch (action.type) {
		case GET_CHANNEL_EVENTS: {
			return defaultHandler(state, action, 'channelEvents');
		}
		case GET_CHANNEL_DEVICE_TYPES: {
			return defaultHandler(state, action, 'channelDevices');
		}
		case GET_USER_SURVEY: {
			return defaultHandler(state, action, 'sessionUserSurvey');
		}
		case GET_CHANNEL_BROWSERS: {
			return defaultHandler(state, action, 'channelBrowsers');
		}
		case GET_CHANNEL_AUDIENCE: {
			return defaultHandler(state, action, 'channelAudience');
		}
		case GET_CHANNEL_REGISTRATIONS: {
			return defaultHandler(state, action, 'channelRegistrations');
		}
		case GET_CHANNEL_DURATION: {
			return defaultHandler(state, action, 'channelDuration');
		}
		case GET_CHANNEL_VIEWS: {
			return defaultHandler(state, action, 'channelViews');
		}
		case GET_CHANNEL_WATCHTIME: {
			return defaultHandler(state, action, 'channelWatchtime');
		}
		case GET_CHANNEL_RETENTION_AVERAGE: {
			return defaultHandler(state, action, 'channelRetentionAverage');
		}

		case GET_CHANNEL_RETENTION_BUCKETS: {
			return defaultHandler(state, action, 'channelRetentionBuckets');
		}
		case GET_CHANNEL_REGIONS: {
			return defaultHandler(state, action, 'channelRegions');
		}
		case GET_EVENT_DEVICE_TYPES: {
			return defaultHandler(state, action, 'eventDevices');
		}
		case GET_EVENT_REGIONS: {
			return defaultHandler(state, action, 'eventRegions');
		}
		case GET_EVENT_VIEWS: {
			return defaultHandler(state, action, 'eventViews');
		}
		case GET_EVENT_DURATION: {
			return defaultHandler(state, action, 'eventDuration');
		}
		case GET_EVENT_RETENTION_AVERAGE: {
			return defaultHandler(state, action, 'eventRetentionAverage');
		}
		case GET_EVENT_RETENTION_BUCKETS: {
			return defaultHandler(state, action, 'eventRetentionBuckets');
		}
		case GET_EVENT_REGISTRATION_ANALYTICS: {
			return defaultHandler(state, action, 'eventRegistrations');
		}
		case GET_EVENT_EMAILS: {
			return defaultHandler(state, action, 'eventEmails');
		}
		case GET_EVENT_WATCHTIME: {
			return defaultHandler(state, action, 'eventWatchtime');
		}
		case GET_EVENT_BROWSERS: {
			return defaultHandler(state, action, 'eventBrowsers');
		}
		case GET_EVENT_SESSIONS: {
			return defaultHandler(state, action, 'eventSessions');
		}
		case GET_EVENT_LANGUAGES: {
			return defaultHandler(state, action, 'eventLanguages');
		}
		case GET_EVENT_COUNTRY_MAP: {
			return defaultHandler(state, action, 'eventMapData');
		}
		case GET_SESSION_DEVICE_TYPES: {
			return defaultHandler(state, action, 'sessionDevices');
		}
		case GET_SESSION_REGIONS: {
			return defaultHandler(state, action, 'sessionRegions');
		}
		case GET_SESSION_VIEWS: {
			return defaultHandler(state, action, 'sessionViews');
		}
		case GET_SESSION_DURATION: {
			return defaultHandler(state, action, 'sessionDuration');
		}
		case GET_SESSION_RETENTION_AVERAGE: {
			return defaultHandler(state, action, 'sessionRetentionAverage');
		}
		case GET_SESSION_REGISTRATIONS: {
			return defaultHandler(state, action, 'sessionRegistrations');
		}
		case GET_SESSION_EMAILS: {
			return defaultHandler(state, action, 'sessionEmails');
		}
		case GET_SESSION_WATCHTIME: {
			return defaultHandler(state, action, 'sessionWatchtime');
		}
		case GET_SESSION_BROWSERS: {
			return defaultHandler(state, action, 'sessionBrowsers');
		}
		case GET_SESSION_LANGUAGES: {
			return defaultHandler(state, action, 'sessionLanguages');
		}
		case GET_SESSION_TIMELINE: {
			return defaultHandler(state, action, 'sessionTimeline');
		}
		case GET_SESSION_COUNTRY_MAP: {
			return defaultHandler(state, action, 'sessionMapData');
		}
		case GET_HIGHEST_CONCURRENT: {
			return defaultHandler(state, action, 'sessionHighestConcurrent');
		}
		case SET_TIME_RANGE: {
			return {
				...state,
				['timeRange']: {
					...state['timeRange'],
					data: action.payload
				}
			};
		}
		case SET_AUDIENCE_LISTS_FILTER: {
			return {
				...state,
				audienceLists: {
					data: action.payload
				}
			};
		}
		default:
			return state;
	}
}
